<template>
  <div class="trbo-a-file-upload-wrapper">
    <div class="trbo-a-file-upload__description">
      {{ questionData.description }}
    </div>
    <spinner v-if="loading" />
    <ul v-if="!loading && files.length" class="trbo-a-file-upload__file-list">
      <li v-for="file in files" :key="file.id" class="trbo-a-file-upload__file">
        <span>{{ file.name }}</span>
        <button
          :id="`remove_upload_${file.id}`"
          class="trbo-a-file-upload__remove-file-button"
          :aria-label="$t('wizard.removeFile')"
          @click.prevent="$refs.upload.remove(file)"
        >
          <icon
            name="close"
            class="trbo-a-file-upload__remove-file-icon"
            :aria-labelledby="`remove_upload_${file.id}`"
          />
        </button>
      </li>
    </ul>
    <file-upload
      ref="upload"
      v-model="files"
      input-id="file-upload"
      accept="image/png,image/gif,image/jpeg,image/webp,application/pdf"
      :multiple="true"
      :drop="true"
      :size="maxFileSizeInBytes"
      :custom-action="uploadFile"
      :class="classes"
      @input-file="inputFile"
      @input-filter="inputFilter"
    >
      <icon name="upload" class="trbo-a-file-upload__icon" />
      <div :id="uploadLabelId" class="trbo-a-file-upload__drag_drop_text">
        {{ $t('wizard.clickToAddFiles') }}
      </div>
    </file-upload>
  </div>
</template>

<script>
// @flow
import VueUploadComponent from 'vue-upload-component'

import Icon from '../../../Icon/Icon.vue'
import Spinner from '../../../Spinner/Spinner.vue'
import { uploadFileToS3, deleteFileFromS3 } from '../../../../../common/awsS3'

export default {
  components: {
    fileUpload: VueUploadComponent,
    Icon,
    Spinner
  },
  props: {
    question: {
      type: Object, // WizardPage
      required: true
    },
    questionData: {
      type: Object, // WizardPageData<InputData>
      required: true
    },
    selected: {
      type: Array,
      default: () => ([])
    }
  },
  data () {
    return {
      fileUrls: [],
      files: [],
      loading: false,
      maxFileSizeInBytes: 10485760, // 10MB in bytes
      isFocused: false
    }
  },
  computed: {
    classes () {
      return {
        'trbo-a-file-upload__uploader': true,
        'trbo-a-file-upload__uploader--is-focused': this.isFocused
      }
    },
    uploadLabelId () {
      return `upload-label-${this.question.id}`
    }
  },
  mounted () {
    const input = this.$refs.upload?.$el.querySelector('input')
    if (input) {
      input.addEventListener('focus', this.handleFocus)
      input.addEventListener('blur', this.handleBlur)
      input.setAttribute('aria-labelledby', this.uploadLabelId)
    }

    this.$refs.upload?.$el.querySelector('label')?.setAttribute('aria-labelledby', this.uploadLabelId)
  },
  beforeDestroy () {
    const input = this.$refs.upload?.$el.querySelector('input')
    if (!input) return

    input.removeEventListener('focus', this.handleFocus)
    input.removeEventListener('blur', this.handleBlur)
  },
  methods: {
    inputFilter (newFile, oldFile, prevent) {
      // Add file
      if (newFile && !oldFile) {
        if (!/\.(jpeg|jpe|jpg|gif|png|webp|pdf)$/i.test(newFile.name)) {
          alert('We do not support this file format. Please upload a gif, jpg, jpeg, png, webp, or pdf file.')
          return prevent()
        }

        if (newFile.size > this.maxFileSizeInBytes) {
          alert("The file you want to upload is too large. Please make sure it's below 10 MB and try again.")
          return prevent()
        }
      }
    },
    async inputFile (newFile, oldFile) {
      // Add new file
      if (newFile && !oldFile) {
        this.loading = true
      }

      // Update file
      if (newFile && oldFile) {
        // Upload error
        if (newFile.error !== oldFile.error) {
          this.loading = false
        }

        // Uploaded successfully
        if (newFile.success !== oldFile.success) {
          this.loading = false
        }
      }

      if (!newFile && oldFile) {
        // Remove file
        this.loading = true

        if (oldFile.success && oldFile.key) {
          this.fileUrls = this.fileUrls.filter(url => !url.includes(oldFile.key))
          this.$emit('submit', { value: [...this.fileUrls] })
          await deleteFileFromS3(this.$store.state.customerId, this.$store.state.subscriberId, oldFile.key)
        }

        this.loading = false
      }

      // Auto upload
      if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
        if (!this.$refs.upload.active) {
          this.$refs.upload.active = true
        }
      }
    },
    async uploadFile (file, _component) {
      const uploadedFile = await uploadFileToS3(this.$store.state.customerId, this.$store.state.subscriberId, file.file)

      if (uploadedFile) {
        await this.$refs.upload.update(file, { key: uploadedFile.key })
        this.fileUrls.push(uploadedFile.fullPath)
        this.$emit('submit', { value: [...this.fileUrls] })
      } else {
        alert('Unexpected error when saving the file. Please try again to upload the same file.')
      }
    },
    handleFocus () {
      this.isFocused = true
    },
    handleBlur () {
      this.isFocused = false
    }
  }
}
</script>

<style src="./custom-vue-upload-component.scss" />
<style scoped src="./file-upload.scss" />
